import { useCallback, useEffect, useState, useRef } from 'react';
import { fetchOverview, searchApplications } from '../api';
import { DEFAULT_FILTERS, PAGE_SIZE } from '../constants';
import { ApplicationLearningSummary, FilterState, TabType } from '../types';

type SearchType = 'organization_name' | 'department_name' | 'specialist_query' | 'student_query';

interface UseApplicationsListReturn {
  items: ApplicationLearningSummary[];
  totalItems: number;
  totalPages: number;
  page: number;
  loading: boolean;
  error: string | null;
  cachedAt: string | null;
  searchQuery: string;
  searchType: SearchType;
  setSearchType: (type: SearchType) => void;
  filters: FilterState;
  appliedFilters: FilterState;
  setPage: (p: number) => void;
  setSearchQuery: (q: string) => void;
  setFilters: (f: FilterState) => void;
  applyFilters: () => void;
  resetFilters: () => void;
  reload: () => void;
}

const STORAGE_KEY = 'monitoringPageFiltersState';

export function useApplicationsList(activeTab: TabType = 'list'): UseApplicationsListReturn {
  const [initialState] = useState(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          page: parsed.page || 1,
          searchQuery: parsed.searchQuery || '',
          searchType: (parsed.searchType as SearchType) || 'organization_name',
          filters: { ...DEFAULT_FILTERS, ...parsed.filters },
          appliedFilters: { ...DEFAULT_FILTERS, ...parsed.appliedFilters },
        };
      }
    } catch (e) {
      console.error("Ошибка чтения сохраненных фильтров", e);
    }
    return {
      page: 1,
      searchQuery: '',
      searchType: 'organization_name' as SearchType,
      filters: DEFAULT_FILTERS,
      appliedFilters: DEFAULT_FILTERS,
    };
  });

  const [items, setItems] = useState<ApplicationLearningSummary[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPageState] = useState(initialState.page);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cachedAt, setCachedAt] = useState<string | null>(null);

  const [searchQuery, setSearchQueryState] = useState(initialState.searchQuery);
  const [searchType, setSearchTypeState] = useState<SearchType>(initialState.searchType);
  const [debouncedQuery, setDebouncedQuery] = useState(initialState.searchQuery);
  const [filters, setFilters] = useState<FilterState>(initialState.filters);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(initialState.appliedFilters);

  const prevSearch = useRef(initialState.searchQuery);
  const prevSearchType = useRef(initialState.searchType);
  const prevFilters = useRef(initialState.appliedFilters);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    const searchChanged = prevSearch.current !== debouncedQuery;
    const typeChanged = prevSearchType.current !== searchType;
    const filtersChanged = JSON.stringify(prevFilters.current) !== JSON.stringify(appliedFilters);

    if (searchChanged || typeChanged || filtersChanged) {
      setPageState(1);
      prevSearch.current = debouncedQuery;
      prevSearchType.current = searchType;
      prevFilters.current = appliedFilters;
    }
  }, [debouncedQuery, searchType, appliedFilters]);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      page,
      searchQuery,
      searchType,
      filters,
      appliedFilters
    }));
  }, [page, searchQuery, searchType, filters, appliedFilters]);

  const fetchData = useCallback(
    async (pg: number, q: string, type: SearchType, f: FilterState, tab: TabType) => {
      setLoading(true);
      setError(null);
      try {
        const hasActiveSearch =
          (q || '').trim() !== '' ||
          (f.overall_status || '') !== '' ||
          (f.nearest_deadline_from || '') !== '' ||
          (f.nearest_deadline_to || '') !== '' ||
          (f.sort_by || '') !== '' ||
          (f.department_name?.trim() || '') !== '' ||
          (f.specialist_query?.trim() || '') !== '' ||
          (f.student_query?.trim() || '') !== '';

        const isFetchAll = tab === 'kanban';

        const data = hasActiveSearch
          ? await searchApplications(q, type, pg, PAGE_SIZE, f, isFetchAll)
          : await fetchOverview(pg, PAGE_SIZE, isFetchAll);

        setItems(data.items);
        setTotalItems(data.total);
        if (data.items.length > 0) {
          setCachedAt(data.items[0].cached_at);
        }
      } catch (e: any) {
        setError(e.message ?? 'Ошибка загрузки данных');
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchData(page, debouncedQuery, searchType, appliedFilters, activeTab);
  }, [page, debouncedQuery, searchType, appliedFilters, activeTab, fetchData]);

  const setPage = (p: number) => setPageState(p);
  const setSearchQuery = (q: string) => setSearchQueryState(q);
  const setSearchType = (t: SearchType) => setSearchTypeState(t);
  const applyFilters = () => setAppliedFilters({ ...filters });
  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
  };
  const reload = () => fetchData(page, debouncedQuery, searchType, appliedFilters, activeTab);

  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  return {
    items,
    totalItems,
    totalPages,
    page,
    loading,
    error,
    cachedAt,
    searchQuery,
    searchType,
    setSearchType,
    filters,
    appliedFilters,
    setPage,
    setSearchQuery,
    setFilters,
    applyFilters,
    resetFilters,
    reload,
  };
}